<script setup lang="ts">
import LoyaltyInputRewardForm from '@/views/loyalty/components/LoyaltyInputRewardForm.vue'
import { useRewardForm } from '@/components/rewards/compmosable/useRewardForm'
import { isRewardInputDisplayed } from '@/components/rewards/utils/rewardHelpers'

import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { ContestGameReward } from '@/types/types'
import type { MessageTemplate, RewardTypeSlug } from './types/global'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:reward', reward: ContestGameReward): void
}>()
const props = defineProps<{
  reward: ContestGameReward
  messageTemplate: MessageTemplate
  messageTemplateQrcode: MessageTemplate
}>()

const notification = useNotification()
const { t } = useI18n()

const { isVoucherSupported } = useReward()
const { rewardVouchers } = useRewardForm()
const { getStoresByPermission } = useStore()
const { noSpecialCharacters } = useRegex()

const hasMessageContentError = ref(false)

const rewardNotificationStoreIds = computed(() =>
  getStoresByPermission('reward.read').map((store: { id: number }) => store.id)
)

const editingReward = ref<ContestGameReward>({
  ...props.reward,
  value: props.reward?.value ? props.reward.value / 100 : null
})
const hasGiftPoint = ref<boolean>(editingReward.value.points > 0)

const mailSubject = ref(props.messageTemplate.subject)
const selectedVoucher = ref<number | null>(
  rewardVouchers.value.find((voucher) => voucher.slug === editingReward.value?.rewardTypeSlug)?.id
)

const shouldOverrideChange = computed(() => {
  const rewardOverrideIsNull =
    editingReward.value.notificationTextOverride?.length === 0 || editingReward.value.notificationTextOverride === null
  const isNotDefault = editingReward.value.notificationTextOverride !== props.messageTemplate.content
  const isNotQrCodeDefault = editingReward.value.notificationTextOverride !== props.messageTemplateQrcode.content

  return (rewardOverrideIsNull && isNotDefault) || (rewardOverrideIsNull && isNotQrCodeDefault)
})

const fillTextOverride = () => {
  if (editingReward.value.rewardTypeSlug === 'points' && editingReward.value.hasQrcode) {
    editingReward.value.notificationTextOverride = t(
      'pages.contests.contestsCreatePage.preview.messageTemplate.content.points',
      {
        rewardTag: '{{ reward }}',
        webAppLink: '{{ customerApp }}'
      }
    )
  } else {
    editingReward.value.notificationTextOverride = editingReward.value.hasQrcode
      ? props.messageTemplateQrcode.content
      : props.messageTemplate.content
  }
}

const channelType = computed(() => {
  if (editingReward.value.hasQrcode) {
    return props.messageTemplateQrcode.messageChannelId === 1 ? 'sms' : 'mail'
  } else {
    return props.messageTemplate.messageChannelId === 1 ? 'sms' : 'mail'
  }
})

const invalidCharacters = computed(
  () => channelType.value === 'sms' && noSpecialCharacters(editingReward.value?.notificationTextOverride)
)

const canSavePoints = computed(() => {
  return hasGiftPoint.value || rewardTypeSlug.value === 'points'
})

const rewardTypeSlug = computed((): RewardTypeSlug => {
  const foundVoucher = rewardVouchers.value.find((voucher) => voucher.id === editingReward.value?.rewardTypeId)
  return foundVoucher ? foundVoucher?.slug : editingReward.value.rewardTypeSlug
})

const saveReward = () => {
  if (invalidCharacters.value) {
    notification.create('error', {
      text: t('ui.error.strings.unauthorizedEmoji'),
      timeout: 2000
    })
    return
  }

  emit('update:reward', {
    ...editingReward.value,
    points: canSavePoints.value ? editingReward.value.points : null,
    rewardTypeSlug: rewardTypeSlug.value,
    value: editingReward.value?.value ? editingReward.value.value * 100 : null
  })
  emit('close')
}

const toggleHasPoint = () => {
  editingReward.value.points =
    editingReward.value.points === null && hasGiftPoint.value ? 10 : editingReward.value.points
  fillTextOverride()
}

const updateVoucher = (selectedId: number) => {
  selectedVoucher.value = selectedId
  editingReward.value.rewardTypeId = selectedId
  editingReward.value.rewardTypeSlug = rewardVouchers.value.find((voucher) => voucher.id === selectedId)?.slug

  if (selectedVoucher.value === 1) {
    editingReward.value.notificationTextOverride = editingReward.value.hasQrcode
      ? props.messageTemplateQrcode.content
      : props.messageTemplate.content
  } else {
    editingReward.value.notificationTextOverride = t(
      `pages.contests.contestsCreatePage.preview.messageTemplateQrcode.content.voucher`,
      {
        rewardTag: '{{ reward }}',
        voucherTag: '{{ voucher }}'
      }
    )
  }
}

const isDisplayed = (slug = null) => {
  const isDisplayed = isRewardInputDisplayed(selectedVoucher.value, slug)
  return isDisplayed
}

const customVariables = computed<CustomVars[]>(() => [
  {
    type: 'variable',
    code: 'points',
    label: t('global.customVars.points.name'),
    icon: 'HeroStarIconSolid',
    disabledMessage: t('global.customVars.points.disabledMessage'),
    length: editingReward.value?.points?.toString()?.length,
    example: editingReward.value?.points?.toString(),
    disabled: !canSavePoints.value || editingReward.value.points === 0
  },
  {
    type: 'variable',
    code: 'reward',
    label: t('global.customVars.reward.name'),
    icon: 'HeroGiftIconSolid',
    disabledMessage: t('global.customVars.reward.disabledMessage'),
    length: editingReward.value ? editingReward.value.name?.length : 0,
    example: editingReward.value?.name,
    disabled: !editingReward.value?.name
  },
  {
    type: 'variable',
    code: 'voucher',
    label: t('global.customVars.voucher.name'),
    icon: 'HeroReceiptPercentIconSolid',
    length: 16,
    example: 'pngo.fr/voucher',
    disabled: !selectedVoucher.value || selectedVoucher.value === 1
  },
  {
    type: 'webapp',
    code: 'qrcodeLink',
    label: t('global.customVars.qrcodeLink.name'),
    icon: 'HeroQrCodeIconSolid',
    length: 16,
    example: 'pngo.fr/qrcode',
    disabled: !editingReward.value.hasQrcode || rewardTypeSlug.value === 'points'
  },
  {
    type: 'webapp',
    code: 'customerApp',
    label: t ? t('global.customVars.customerApp.rewards.name') : null,
    icon: 'HeroGlobeAltIconSolid',
    length: 16,
    example: 'pngo.fr/webapp'
  },
  {
    type: 'webapp',
    code: 'customerApp(customer-profile)',
    label: t ? t('global.customVars.customerApp.customerProfile.name') : null,
    icon: 'HeroGlobeAltIconSolid',
    length: 16,
    example: 'pngo.fr/webapp'
  },
  {
    type: 'webapp',
    code: 'customerApp(wallet)',
    label: t ? t('global.customVars.customerApp.wallet.name') : null,
    icon: 'HeroGlobeAltIconSolid',
    length: 16,
    example: 'pngo.fr/webapp'
  }
])

if (shouldOverrideChange.value) {
  fillTextOverride()
}

const handleMessageContentError = (error: boolean) => {
  hasMessageContentError.value = error
}
</script>

<template>
  <div class="text-left">
    <div class="flex flex-col gap-4">
      <div v-if="editingReward.rewardTypeSlug !== 'points' && isVoucherSupported" class="space-y-2">
        <label for="name" class="block text-sm font-medium text-foreground sm:mt-px">
          {{ t(`pages.loyaltyPage.edit.fields.rewardType`) }}
        </label>
        <ComboboxSelect
          value-key="id"
          label-key="text"
          trigger-class="w-auto"
          :placeholder="t('pages.loyaltyPage.edit.fields.choose')"
          :options="rewardVouchers"
          :model-value="selectedVoucher"
          @update:model-value="updateVoucher"
        />
      </div>

      <LoyaltyInputRewardForm
        v-if="rewardTypeSlug === 'free_product'"
        ref="externalId"
        v-model="editingReward.externalId"
        title="externalId"
      />

      <LoyaltyInputRewardForm
        v-if="isDisplayed('discountValuePercent')"
        ref="discountValuePercent"
        v-model="editingReward.value"
        type="number"
        step="0.01"
        title="discountValuePercent"
      />
      <LoyaltyInputRewardForm
        v-if="isDisplayed('voucherDiscountAmount')"
        ref="voucherDiscountAmount"
        v-model="editingReward.value"
        type="number"
        step="0.01"
        title="voucherDiscountAmount"
      />
      <LoyaltyInputRewardForm
        v-if="isDisplayed('minimumOrderAmount')"
        ref="minimumOrderAmount"
        v-model="editingReward.minimumOrderAmount"
        type="number"
        step="0.01"
        title="minimumOrderAmount"
      />

      <BaseSwitch
        v-if="rewardTypeSlug !== 'points'"
        id="hasGiftGivePoint"
        v-model="hasGiftPoint"
        :label="t('pages.contests.contestsCreatePage.prices.form.price.hasGiftGivePoint')"
        @click="toggleHasPoint"
      />
      <AppFormInput
        v-if="canSavePoints"
        id="numberOfPoint"
        v-model="editingReward.points"
        type="number"
        placeholder="0"
        :min="0"
        :label="t('pages.contests.contestsCreatePage.prices.form.price.labelPoint')"
      />

      <LoyaltyInputRewardForm
        v-if="isDisplayed('validityInDays')"
        ref="validityInDays"
        v-model="editingReward.validityInDays"
        type="number"
        title="validityInDays"
      />

      <BaseSwitch
        v-if="rewardTypeSlug !== 'points'"
        id="hasQrcode"
        v-model="editingReward.hasQrcode"
        :label="t('pages.contests.contestsCreatePage.prices.form.price.hasQrCode')"
        @click="fillTextOverride"
      />

      <div v-if="channelType === 'mail'" class="flex flex-col gap-3">
        <label
          for="messageSubject"
          class="block cursor-not-allowed text-sm font-medium text-foreground sm:mt-px sm:pt-2"
        >
          {{ t('pages.contests.contestsCreatePage.message.form.fields.emailObject') }}
        </label>
        <AppFormInput
          id="messageSubject"
          v-model="mailSubject"
          type="text"
          :disabled="true"
          class="cursor-not-allowed opacity-50"
        />
      </div>

      <TextareaWithVars
        v-model="editingReward.notificationTextOverride"
        label="about"
        :max-length="channelType === 'sms' ? 160 : 10000"
        :i18n-strings="{
          textareaLabel: t(
            `pages.contests.contestsCreatePage.message.form.fields.notificationTextOverride.${channelType}`
          ),
          characters: t('global.words.characters'),
          insertVar: t('global.customVars.insertLabel'),
          errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji'),
          customValue: {
            placeholder: t('global.customVars.customValue.placeholder'),
            create: t('global.customVars.customValue.add'),
            update: t('global.customVars.customValue.modify'),
            back: t('global.customVars.customValue.back')
          }
        }"
        :custom-vars="customVariables"
        :sanitize-for-gsm="channelType === 'sms'"
        :required-store-ids="rewardNotificationStoreIds"
        @has-error="handleMessageContentError"
      />
    </div>

    <div class="mt-10 flex items-center justify-center gap-2">
      <Button variant="outline" class="flex-1" @click="emit('close')">
        {{ t('global.words.cancel') }}
      </Button>
      <Button variant="default-primary" class="flex-1" :disabled="hasMessageContentError" @click="saveReward">
        {{ t('global.words.submit') }}
      </Button>
    </div>
  </div>
</template>
